import React from 'react';
import { useAppDispatch, useAppSelector } from 'store/store';
import { asyncFetchElection } from 'store/reducers/elections/thunks';
import { asyncFetchUser } from 'store/reducers/user/thunks';
import {
    Outlet,
    Link,
    useParams,
    useLocation,
    useNavigate,
} from 'react-router-dom';
import './ElectionOutlet.scss';

import { IElection } from 'store/reducers/elections/types';
import { IEmployee, IStudent } from 'store/reducers/user/types';
import { clearElection } from 'store/reducers/elections';
import { isStudent } from 'js/utils';
import { currentSeason, formatSchedule, SeasonState } from 'js/season';
import { facultyGradientByName } from 'theme/faculties';
import GlassButton from 'components/redesign/GlassButton';
import Tooltip from 'components/redesign/Tooltip';
import FaqBlock from 'components/redesign/FaqBlock';
import ResultsList from 'components/redesign/ResultsList';
import LoginModal from 'forms/modals/LoginModal';
import LoginForm from 'forms/forms/LoginForm';
import { ReactComponent as CheckIcon } from 'assets/icons/redesign/check-circle-fill.svg';
import { ReactComponent as LockIcon } from 'assets/icons/Lock.svg';
import { computeOutcome, ElectionWithResults } from './results';

type ActionKind =
    | 'guest'
    | 'no-access'
    | 'before-start'
    | 'voted'
    | 'can-vote'
    | 'closed'
    | 'inactive'
    | 'no-candidates';

interface ActionState {
    kind: ActionKind;
}

export function hasElectionAccess(
    user: IStudent | IEmployee,
    election: IElection
): boolean {
    if (!isStudent(user)) return true;
    const facultyMatches =
        !election.faculty || election.faculty.id === user.group.faculty.id;
    const hostelId =
        typeof user.hostel === 'number' ? user.hostel : user.hostel?.id;
    return (
        facultyMatches && (!election.hostel || election.hostel.id === hostelId)
    );
}
function resolveActionState(
    user: IStudent | IEmployee | undefined,
    election: IElection,
    season: SeasonState
): ActionState {
    if (season === 'soon') {
        return { kind: 'before-start' };
    }
    if (season === 'closed' || season === 'results') {
        return { kind: 'closed' };
    }
    if (!user) return { kind: 'guest' };
    if (!hasElectionAccess(user, election)) {
        return { kind: 'no-access' };
    }
    if (election.voted) return { kind: 'voted' };
    if (!election.is_active) return { kind: 'inactive' };
    if (election.candidates.length === 0) return { kind: 'no-candidates' };
    return { kind: 'can-vote' };
}

function actionTooltipText(
    kind: ActionKind,
    season: SeasonState,
    election: IElection
): string | undefined {
    if (kind === 'before-start') {
        return `Голосование начнётся ${new Date(
            election.date_start
        ).toLocaleString('ru-RU', {
            day: 'numeric',
            month: 'long',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'Europe/Moscow',
        })} мск.`;
    }
    if (kind === 'no-access') {
        return 'Эти выборы доступны только студентам выбранного подразделения.';
    }
    if (kind === 'closed') {
        return season === 'closed'
            ? 'Голосование завершено, итоги готовятся.'
            : 'Голосование завершено — итоги ниже на странице.';
    }
    if (kind === 'inactive') {
        return 'Эти выборы сейчас недоступны — попробуй зайти позже.';
    }
    if (kind === 'no-candidates') {
        return (
            'Кандидатов пока нет — голосование по этим выборам ' +
            'не проводится.'
        );
    }
    return undefined;
}
function ActionControl({
    state,
    season,
    election,
    onLogin,
    onVote,
}: {
    state: ActionState;
    season: SeasonState;
    election: IElection;
    onLogin: () => void;
    onVote: () => void;
}): React.ReactElement {
    if (state.kind === 'guest') {
        return (
            <span className="rd-e-cta rd-e-fill">
                <GlassButton tone="green" onClick={onLogin}>
                    Войти чтобы проголосовать
                </GlassButton>
            </span>
        );
    }

    if (state.kind === 'can-vote') {
        return (
            <span className="rd-e-cta rd-e-fill">
                <GlassButton tone="green" onClick={onVote}>
                    Перейти к голосованию
                </GlassButton>
            </span>
        );
    }

    if (state.kind === 'voted') {
        return (
            <span className="rd-e-cta rd-e-fill">
                <span className="rd-btn rd-btn--green rd-e-cta--static">
                    <span className="rd-e-cta__icon" aria-hidden="true">
                        <CheckIcon />
                    </span>
                    Твой голос принят
                </span>
            </span>
        );
    }
    const tooltipText = actionTooltipText(state.kind, season, election);
    return (
        <Tooltip
            id={`rd-e-cta-tt-${state.kind}`}
            content={tooltipText}
            className="rd-e-cta"
        >
            <GlassButton tone="green" disabled>
                <span className="rd-btn__icon" aria-hidden="true">
                    <LockIcon />
                </span>
                Перейти к голосованию
            </GlassButton>
        </Tooltip>
    );
}
function ElectionOutlet(): React.ReactElement {
    const dispatch = useAppDispatch();
    const { electionId } = useParams();
    const { search } = useLocation();
    const navigate = useNavigate();
    const { user } = useAppSelector(state => state.userStore);
    const { election } = useAppSelector(state => state.electionsStore);
    const [loginOpen, setLoginOpen] = React.useState(false);
    const [loadState, setLoadState] = React.useState<
        'loading' | 'ready' | 'error'
    >('loading');
    const [request, retry] = React.useReducer(value => value + 1, 0);
    const season = currentSeason(election);

    React.useEffect(() => {
        let active = true;
        setLoadState('loading');
        dispatch(asyncFetchElection(Number(electionId)))
            .unwrap()
            .then(
                () => {
                    if (active) setLoadState('ready');
                },
                () => {
                    if (active) setLoadState('error');
                }
            );
        return () => {
            active = false;
            dispatch(clearElection());
        };
    }, [dispatch, electionId, user, request]);

    const onLoginSuccess = React.useCallback(async () => {
        setLoginOpen(false);
        await dispatch(asyncFetchUser()).unwrap();
        if (electionId) {
            await dispatch(asyncFetchElection(Number(electionId))).unwrap();
        }
    }, [dispatch, electionId]);

    const onVote = React.useCallback(() => {
        navigate(`/elections/${electionId}/vote${search}`);
    }, [navigate, electionId, search]);

    if (loadState === 'error') {
        return (
            <div className="rd-container rd-sections">
                <div className="rd-e-error" role="alert">
                    <h1 className="rd-h1">Не удалось загрузить выборы</h1>
                    <p>Проверь соединение и попробуй ещё раз.</p>
                    <button
                        type="button"
                        className="rd-btn rd-btn--green"
                        onClick={retry}
                    >
                        Повторить
                    </button>
                </div>
            </div>
        );
    }

    if (loadState === 'loading' || !election) {
        return (
            <div className="rd-container rd-sections" role="status">
                Загружаем выборы…
            </div>
        );
    }

    const state = resolveActionState(user, election, season);
    const gradient = facultyGradientByName(
        election.faculty?.name,
        election.faculty?.short_name
    );
    const resultsData = election as ElectionWithResults;
    const outcome = computeOutcome(resultsData);
    const start = new Date(election.date_start);
    const end = new Date(election.date_end);
    let statusLabel = {
        soon: 'Скоро',
        live: 'Идёт голосование',
        closed: 'Завершено',
        results: 'Итоги опубликованы',
    }[season];
    if (state.kind === 'inactive') statusLabel = 'Голосование недоступно';
    if (state.kind === 'voted') statusLabel = 'Голос учтён';
    if (outcome && season === 'closed') statusLabel = 'Итоги опубликованы';

    return (
        <div className="rd-container rd-sections rd-election-page">
            <header className="rd-election-header">
                <Link className="rd-election-back" to={`/${search}`}>
                    ← Все выборы
                </Link>
                <div
                    className="rd-election-summary"
                    style={{ background: gradient }}
                >
                    <div className="rd-election-heading">
                        <h1 className="rd-h1">{election.name}</h1>
                    </div>
                    <div className="rd-election-schedule">
                        <time
                            className="rd-schedule-details"
                            dateTime={election.date_start}
                        >
                            <span>{formatSchedule(start, end)}</span>
                        </time>
                        <span className="rd-schedule-status">
                            {statusLabel}
                        </span>
                    </div>
                </div>
                {['no-access', 'inactive', 'no-candidates'].includes(
                    state.kind
                ) && (
                    <p className="rd-election-state" role="status">
                        {actionTooltipText(state.kind, season, election)}
                    </p>
                )}
            </header>
            {outcome ? (
                <section className="rd-block">
                    <h2 className="rd-h2">Итоги голосования</h2>
                    <ResultsList outcome={outcome} />
                </section>
            ) : (
                <Outlet />
            )}
            {['guest', 'can-vote'].includes(state.kind) && (
                <div className="rd-vote-action">
                    <ActionControl
                        state={state}
                        season={season}
                        election={election}
                        onLogin={() => setLoginOpen(true)}
                        onVote={onVote}
                    />
                </div>
            )}

            {outcome && (
                <details className="rd-candidate-archive">
                    <summary>Кандидаты и программы</summary>
                    <Outlet />
                </details>
            )}
            <FaqBlock />

            <LoginModal
                open={loginOpen}
                onCancel={() => setLoginOpen(false)}
                header="Авторизация"
                subHeader="Откроет доступ к голосованию на выборах"
                form={<LoginForm onSuccess={onLoginSuccess} />}
            />
        </div>
    );
}

export default React.memo(ElectionOutlet);
